import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Globe, ShoppingCart, TrendingUp, AlertTriangle, Package, DollarSign, MapPin } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article5 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-amber-500 text-white mr-4">
              <ShoppingCart className="h-3 w-3 mr-1" />
              E-commerce
            </Badge>
            <span className="text-sm text-gray-600">Ecommercenews.eu</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>5 min read</span>
          </div>
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.5.title')}
          </h1>

          <img
            src="https://ecommercenews.eu/static/wp-content/uploads/2023/08/Amazon-low-price-FBA-rates-.jpg?w=360&h=240&bg=white&fm=webp"
            alt="Amazon FBA rates"
            className="w-full h-64 object-cover rounded-lg mb-8 bg-white"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.5.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-amber-50 p-6 rounded-lg my-8 border border-amber-200">
              <h3 className="font-bold text-amber-900 mb-4">
                {language === 'ko' ? '핵심 수치' : 'Key Figures'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <h4 className="font-semibold mb-1 flex items-center justify-center text-amber-800">
                    <DollarSign className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'FBA 할증료 (유럽)' : 'FBA Surcharge (Europe)'}
                  </h4>
                  <p className="text-3xl font-bold text-orange-600">1.5%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '10개국 / 4월 17일~' : '10 countries / from Apr 17'}</p>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold mb-1 flex items-center justify-center text-amber-800">
                    <DollarSign className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '할증료 (미국·캐나다)' : 'Surcharge (US/Canada)'}
                  </h4>
                  <p className="text-3xl font-bold text-red-600">3.5%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '유럽보다 2배 이상' : 'More than 2× Europe rate'}</p>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold mb-1 flex items-center justify-center text-amber-800">
                    <MapPin className="mr-1 h-4 w-4" />
                    {language === 'ko' ? 'FBA 적용 국가 수' : 'FBA Countries'}
                  </h4>
                  <p className="text-3xl font-bold text-blue-600">10</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '유럽 전용 도메인 보유국' : 'Dedicated EU webshop domains'}</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.5.section1.title')}
            </h2>
            <p className="mb-6">{t('article.5.p1')}</p>
            <p className="mb-6">{t('article.5.p2')}</p>

            {/* FBA Countries Table */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'FBA 할증료 적용 국가 (4월 17일~)' : 'FBA Surcharge Countries (from April 17)'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                {[
                  { flag: '🇩🇪', name: language === 'ko' ? '독일' : 'Germany' },
                  { flag: '🇬🇧', name: language === 'ko' ? '영국' : 'United Kingdom' },
                  { flag: '🇫🇷', name: language === 'ko' ? '프랑스' : 'France' },
                  { flag: '🇮🇹', name: language === 'ko' ? '이탈리아' : 'Italy' },
                  { flag: '🇪🇸', name: language === 'ko' ? '스페인' : 'Spain' },
                  { flag: '🇵🇱', name: language === 'ko' ? '폴란드' : 'Poland' },
                  { flag: '🇸🇪', name: language === 'ko' ? '스웨덴' : 'Sweden' },
                  { flag: '🇳🇱', name: language === 'ko' ? '네덜란드' : 'Netherlands' },
                  { flag: '🇮🇪', name: language === 'ko' ? '아일랜드' : 'Ireland' },
                  { flag: '🇧🇪', name: language === 'ko' ? '벨기에' : 'Belgium' },
                ].map((country) => (
                  <div key={country.name} className="bg-white rounded p-2 text-center border">
                    <div className="text-xl mb-1">{country.flag}</div>
                    <div className="text-xs font-medium">{country.name}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {language === 'ko' ? '※ 이 10개국 모두 전용 아마존 쇼핑몰 도메인 보유국입니다.' : '※ All 10 countries have a dedicated Amazon webshop domain.'}
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.5.section2.title')}
            </h2>
            <p className="mb-6">{t('article.5.p3')}</p>
            <p className="mb-6">{t('article.5.p4')}</p>

            {/* Surcharge Breakdown */}
            <div className="bg-orange-50 p-6 rounded-lg my-8 border border-orange-200">
              <h3 className="font-bold text-orange-900 mb-4 flex items-center">
                <Package className="mr-2 h-5 w-5" />
                {language === 'ko' ? '할증료 적용 일정 및 범위' : 'Surcharge Schedule & Scope'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-orange-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '서비스' : 'Service'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '적용일' : 'Effective Date'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '할증률' : 'Rate'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '적용 국가' : 'Countries'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">FBA</td>
                      <td className="py-2 px-3">{language === 'ko' ? '2026년 4월 17일' : 'April 17, 2026'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">1.5%</td>
                      <td className="py-2 px-3">{language === 'ko' ? '독일, 영국, 프랑스, 이탈리아, 스페인, 폴란드, 스웨덴, 네덜란드, 아일랜드, 벨기에' : 'DE, UK, FR, IT, ES, PL, SE, NL, IE, BE'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">MCF</td>
                      <td className="py-2 px-3">{language === 'ko' ? '2026년 5월 2일' : 'May 2, 2026'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">1.5%</td>
                      <td className="py-2 px-3">{language === 'ko' ? '독일, 영국, 프랑스, 이탈리아, 스페인' : 'DE, UK, FR, IT, ES'}</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? 'FBA/MCF (미국·캐나다)' : 'FBA/MCF (US/CA)'}</td>
                      <td className="py-2 px-3">{language === 'ko' ? '2026년 4월 (다음 주)' : 'April 2026 (next week)'}</td>
                      <td className="py-2 px-3 text-red-600 font-bold">3.5%</td>
                      <td className="py-2 px-3">{language === 'ko' ? '미국, 캐나다' : 'US, Canada'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'ko' ? '※ 할증료는 판매 가격이 아닌 배송 비용에 적용됩니다. EU 내 FBA 평균 약 €0.05/건.' : '※ Surcharge is applied to shipping costs, not selling price. EU FBA average approx. €0.05 per item.'}
              </p>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.5.section3.title')}
            </h2>
            <p className="mb-6">{t('article.5.p5')}</p>
            <p className="mb-6">{t('article.5.p6')}</p>

            {/* Amazon quote */}
            <blockquote className="border-l-4 border-amber-500 pl-6 py-2 my-6 bg-amber-50 rounded-r-lg">
              <p className="text-gray-700 italic text-sm leading-relaxed">
                {language === 'ko'
                  ? '"비용 절감을 위한 집단적 노력 덕분에 이 할증료는 다른 주요 운송업체보다 현저히 낮습니다. 상황이 변화함에 따라 할증료를 재평가할 것입니다."'
                  : '"Thanks to the collective efforts we have already made to reduce costs, this surcharge is significantly lower than that of other major carriers. We will reassess the surcharge as the situation evolves."'}
              </p>
              <footer className="text-xs text-gray-500 mt-2">— Amazon {language === 'ko' ? '공식 성명' : 'Official Statement'}</footer>
            </blockquote>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.5.section4.title')}
            </h2>
            <p className="mb-6">{t('article.5.p7')}</p>
            <p className="mb-6">{t('article.5.p8')}</p>

            {/* Industry Context */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                {language === 'ko' ? '업계 맥락: 중동 분쟁과 물류 비용' : 'Industry Context: Middle East Conflict & Logistics Costs'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2 text-red-500">▲</span>
                  <span>{language === 'ko' ? '중동 분쟁 이후 등유 가격 두 배 이상 급등 — 3월 사상 최고치 경신' : 'Kerosene prices more than doubled since Middle East conflict — record high in March'}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-red-500">▲</span>
                  <span>{language === 'ko' ? '두바이발 미국행 항공화물 현물 운임 +152% (YoY) 급등' : 'Dubai-to-US air cargo spot rates surged +152% YoY'}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-orange-500">→</span>
                  <span>{language === 'ko' ? '아마존 외 다른 주요 운송업체들도 연료 할증료 도입 또는 인상 중' : 'Other major carriers also introducing or raising fuel surcharges'}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-orange-500">→</span>
                  <span>{language === 'ko' ? 'FBA 유럽 1.5%는 업계 평균보다 낮은 수준 — 아마존 자체 비용 절감 노력 반영' : 'FBA Europe 1.5% is below industry average — reflects Amazon\'s own cost-reduction efforts'}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-blue-500">ℹ</span>
                  <span>{language === 'ko' ? '아마존은 연말 성수기 등 다양한 상황에서 일시적 FBA 요금 인상을 시행해 온 바 있음' : 'Amazon has a history of temporary FBA fee increases (e.g., holiday season)'}</span>
                </li>
              </ul>
            </div>

            {/* Warning Box */}
            <div className="bg-red-50 p-6 rounded-lg my-8 border border-red-200">
              <h3 className="font-bold text-red-900 mb-3 flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                {language === 'ko' ? '판매자 주의사항' : 'Seller Alert'}
              </h3>
              <p className="text-sm text-gray-700">
                {language === 'ko'
                  ? '할증료는 배송 비용에 적용되며, 상품 크기에 따라 실제 부담액이 다릅니다. 아마존은 "상황이 변화함에 따라 할증료를 재평가할 것"이라고 밝혔으므로, 판매자들은 Seller Central 공지를 지속적으로 모니터링할 것을 권장합니다.'
                  : 'The surcharge applies to shipping costs and varies by product dimensions. As Amazon will "reassess the surcharge as the situation evolves", sellers are advised to monitor Seller Central announcements regularly.'}
              </p>
            </div>

            {/* Key Takeaways */}
            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '주요 시사점' : 'Key Takeaways'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="mr-2 text-orange-500">●</span><span>{language === 'ko' ? '4월 17일부터 유럽 10개국 FBA에 1.5% 연료·물류 할증료 부과' : '1.5% fuel & logistics surcharge added to FBA in 10 European countries from April 17'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-orange-500">●</span><span>{language === 'ko' ? '5월 2일부터 5개 주요 유럽 시장 MCF에도 동일 1.5% 적용' : 'Same 1.5% surcharge for MCF in 5 major European markets from May 2'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-red-500">●</span><span>{language === 'ko' ? '미국·캐나다는 유럽보다 높은 3.5% 할증료 적용' : 'US and Canada face a higher 3.5% surcharge vs Europe\'s 1.5%'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-blue-500">●</span><span>{language === 'ko' ? 'EU 내 평균 약 5유로센트/건 — 아마존은 "다른 주요 운송업체보다 현저히 낮다" 강조' : 'EU average ~5 eurocents per item — Amazon stresses "significantly lower than other major carriers"'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-gray-500">●</span><span>{language === 'ko' ? '일시적 조치로 발표 — 중동 분쟁 상황에 따라 재평가 예정' : 'Announced as temporary — will be reassessed as the Middle East situation evolves'}</span></li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Ecommercenews.eu — Amazon introduces transport surcharge for partners.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://ecommercenews.eu/amazon-introduces-transport-surcharge-for-partners/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article5;
